/**
 * ZDatabase - Zod Validation Schemas for Database API
 *
 * Single source of truth for Database API validation.
 * These schemas generate both runtime validation (Zod) and compile-time types (TApi).
 *
 * Admin-panel endpoint - protected by AdminPermissionGuard (PBAC)
 * Base path: /api/admin/database
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  DeployMigrationsBodySchema,
  DeployMigrationsResponseSchema,
  GetMigrationSqlResponseSchema,
  ListMigrationsResponseSchema,
  MarkResolvedBodySchema,
  MarkResolvedResponseSchema
} from "./schemas/migration.schema";
import { ExecuteSqlBodySchema, ExecuteSqlResponseSchema } from "./schemas/sql-query.schema";

/**
 * ZDatabase - Zod API validation schemas for Database Management
 *
 * Single source of truth for Database API:
 * - Runtime validation (backend + frontend)
 * - Type generation (TApi auto-inferred via InferSchemas)
 *
 * Admin endpoint - requires ROOT/admin authentication
 */
export const ZDatabase = {
  /**
   * List all migrations (applied + pending + failed)
   *
   * GET /api/admin/database/migrations
   *
   * Returns all migrations from both database (_prisma_migrations table)
   * and filesystem (migrations directory), merged with status calculation.
   */
  ListMigrations: {
    method: "GET" as const,
    path: "/api/admin/database/migrations",
    Response: ApiSuccessSchema(ListMigrationsResponseSchema)
  },

  /**
   * Get SQL content for a specific migration
   *
   * GET /api/admin/database/migrations/:name/sql
   *
   * Reads the migration.sql file from the migrations directory.
   */
  GetMigrationSql: {
    method: "GET" as const,
    path: "/api/admin/database/migrations/:name/sql",
    PathVars: z.object({ name: z.string() }),
    Response: ApiSuccessSchema(GetMigrationSqlResponseSchema)
  },

  /**
   * Deploy pending migrations
   *
   * POST /api/admin/database/migrations/deploy
   *
   * Runs `prisma migrate deploy` to apply pending migrations.
   * Optionally supports dry-run mode to preview changes.
   */
  DeployMigrations: {
    method: "POST" as const,
    path: "/api/admin/database/migrations/deploy",
    Body: DeployMigrationsBodySchema,
    Response: ApiSuccessSchema(DeployMigrationsResponseSchema)
  },

  /**
   * Mark a failed migration as resolved
   *
   * POST /api/admin/database/migrations/:name/resolve
   *
   * Marks a failed migration as resolved when the issue has been fixed
   * (e.g., via a subsequent corrected migration). Resolved migrations
   * appear collapsed and greyed out in the UI.
   */
  MarkResolved: {
    method: "POST" as const,
    path: "/api/admin/database/migrations/:name/resolve",
    PathVars: z.object({ name: z.string() }),
    Body: MarkResolvedBodySchema,
    Response: ApiSuccessSchema(MarkResolvedResponseSchema)
  },

  /**
   * Execute a SQL query
   *
   * POST /api/admin/database/sql/execute
   *
   * Executes raw SQL against the database.
   * - Read-only mode (default): Only SELECT queries allowed
   * - Danger mode: All queries allowed (INSERT, UPDATE, DELETE, etc.)
   *
   * All queries are logged for audit.
   */
  ExecuteSql: {
    method: "POST" as const,
    path: "/api/admin/database/sql/execute",
    Body: ExecuteSqlBodySchema,
    Response: ApiSuccessSchema(ExecuteSqlResponseSchema)
  }
} as const;
